(function () {
  const App = window.WallArtApp;

  const LAYOUT_LABELS = {
    single: "Single piece",
    pair: "Pair (diptych)",
    triptych: "Triptych"
  };

  const FURNITURE_LABELS = {
    armchair: "armchair",
    sofa_2_seater: "2-seater sofa",
    sofa_3_seater: "3-seater sofa",
    sofa_custom: "custom sofa",
    sofa_large: "large sofa",
    bed_single: "single bed",
    bed_small_double: "small double bed",
    bed_double: "double bed",
    bed_king: "king bed",
    bed_super_king: "super king bed",
    bed_custom: "custom bed",
    sideboard: "sideboard / cabinet",
    console_table: "console table",
    dining_table: "dining table",
    desk: "dining table",
    vanity_unit: "vanity unit",
    bath: "bath",
    kitchen_worktop: "worktop / unit",
    none: "wall"
  };

  const ROOM_COLLECTION_LINKS = {
    living: {
      text: "Browse living room prints",
      url: "/collections/living-room-wall-art"
    },
    bedroom: {
      text: "Browse bedroom prints",
      url: "/collections/bedroom-wall-art"
    },
    bathroom: {
      text: "Browse bathroom prints",
      url: "/collections/bathroom-wall-art"
    },
    hallway: {
      text: "Browse hallway prints",
      url: "/collections/hallway-wall-art"
    },
    dining: {
      text: "Browse all prints",
      url: "/collections/art-prints"
    },
    kitchen: {
      text: "Browse all prints",
      url: "/collections/art-prints"
    }
  };

  const FALLBACK_COLLECTION_LINK = {
    text: "Browse all prints",
    url: "/collections/art-prints"
  };

  const SHOP_CTA_BASE_COLLECTION = "/collections/art-prints";

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function formatMeasurement(value) {
    if (value == null || Number.isNaN(value)) {
      return "—";
    }

    return App.formatValue(value) + " " + App.unitLabel();
  }

  function formatAccessibleMeasurement(value) {
    if (value == null || Number.isNaN(value)) {
      return "unknown";
    }

    return App.formatValue(value) + (App.state.region === "imperial" ? " inches" : " centimetres");
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function escapeAttribute(value) {
    return escapeHtml(value);
  }

  function orientationLabel(value) {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  function layoutLabel(value) {
    return LAYOUT_LABELS[value] || value.replace(/_/g, " ");
  }

  function accessibleLayoutLabel(value) {
    if (value === "single") return "single piece";
    if (value === "pair") return "pair";
    if (value === "triptych") return "triptych";
    return value.replace(/_/g, " ");
  }

  function furnitureLabel(engineResult) {
    const key = engineResult.resolvedInputs && engineResult.resolvedInputs.furnitureTypeKey;
    return FURNITURE_LABELS[key] || "furniture";
  }

  function hasFurniture(engineResult) {
    return engineResult.previewGeometry &&
      engineResult.previewGeometry.anchor &&
      engineResult.previewGeometry.anchor.type !== "none";
  }

  function isDiningTableDisplay(engineResult) {
    const inputs = engineResult.resolvedInputs || {};
    const artwork = engineResult.previewGeometry && engineResult.previewGeometry.artwork;
    const key = inputs.furnitureTypeKey;

    return inputs.roomType === "dining" &&
      (key === "dining_table" || key === "desk") &&
      artwork &&
      artwork.clearanceAboveFurnitureCm == null;
  }

  function isNoHeadboardBedDisplay(engineResult) {
    const inputs = engineResult.resolvedInputs || {};

    return inputs.anchorType === "bed" && inputs.hasHeadboard === false;
  }

  function isLowConsoleOrSideboardDisplay(engineResult) {
    const inputs = engineResult.resolvedInputs || {};
    const anchorTopHeightCm = inputs.anchorTopHeightCm;

    return (inputs.anchorType === "console" || inputs.anchorType === "sideboard") &&
      typeof anchorTopHeightCm === "number" &&
      anchorTopHeightCm < 76;
  }

  function isBathDisplay(engineResult) {
    const inputs = engineResult.resolvedInputs || {};

    return inputs.anchorType === "bath" || inputs.furnitureTypeKey === "bath";
  }

  function isCentreHeightFurnitureDisplay(engineResult) {
    return isBathDisplay(engineResult) ||
      isNoHeadboardBedDisplay(engineResult) ||
      isLowConsoleOrSideboardDisplay(engineResult);
  }

  function centreHeightPlacementText(engineResult) {
    const artwork = engineResult.previewGeometry.artwork;

    if (isNoHeadboardBedDisplay(engineResult)) {
      return "centred at " + formatMeasurement(artwork.centreFromFloorCm) + " for a balanced no-headboard bed placement";
    }

    if (isBathDisplay(engineResult)) {
      return "centred at " + formatMeasurement(artwork.centreFromFloorCm) + ", with clear space above your bath";
    }

    return "centred at " + formatMeasurement(artwork.centreFromFloorCm) + " because this furniture is low";
  }

  function anchorWidth(engineResult) {
    if (hasFurniture(engineResult)) {
      return engineResult.resolvedInputs.anchorWidthCm;
    }

    return engineResult.resolvedInputs.usableWallWidthCm;
  }

  function anchorLabel(engineResult) {
    return hasFurniture(engineResult) ? "furniture" : "usable wall";
  }

  function percentOfAnchor(value, engineResult) {
    return value / anchorWidth(engineResult);
  }

  function alertClass(type) {
    if (type === "error") return "alert-error";
    if (type === "warn") return "alert-warn";
    return "alert-success";
  }

  function warningToAlert(warning) {
    const meta = warning.meta || {};

    switch (warning.code) {
      case "DEFAULTS_USED":
        return null;
      case "UNDERSIZED":
        return {
          type: "warn",
          code: "Undersized",
          msg: "The selected layout sits below the recommended minimum width band."
        };
      case "ABOVE_WIDTH_BAND":
        return {
          type: "warn",
          code: "Above range",
          msg: "The selected layout sits above the recommended maximum width band."
        };
      case "CLEARANCE_TOO_HIGH":
        return {
          type: "warn",
          code: "Clearance high",
          msg: "This placement needs more clearance above the furniture than the ideal range."
        };
      case "LOW_CEILING_LIMITED_SIZE_OPTIONS":
        return {
          type: "info",
          code: "Ceiling height",
          msg: "Larger options were limited by the available wall space, so this keeps better breathing room above the frame."
        };
      case "ORIENTATION_CHANGED_FOR_FIT":
        return {
          type: "info",
          code: "Orientation adjusted",
          msg: "We adjusted the orientation automatically for a better fit."
        };
      case "LAYOUT_CHANGED_FOR_FIT":
        return {
          type: "info",
          code: "Layout adjusted",
          msg: "We adjusted the layout automatically for a better fit."
        };
      case "FURNITURE_CENTRED_WIDTH_LIMITED":
        return {
          type: "warn",
          code: "Width limited",
          msg: "Keeping the artwork centred on the furniture limits the total usable width to about " + formatMeasurement(meta.maxFurnitureCentredWidthCm || 0) + "."
        };
      case "NO_HEADBOARD_LOW_PLACEMENT":
        return {
          type: "warn",
          code: "Estimated bed height",
          msg: "This bedroom result is estimated because the bed height was treated as mattress top height."
        };
      case "BATHROOM_PLACEMENT_CHECK":
        return {
          type: "info",
          code: "Bathroom check",
          msg: "Before hanging, avoid direct splash areas, mirrors, towel rails and powered fittings, and check the wall before drilling."
        };
      case "BATH_PLACEMENT_CHECK":
        return {
          type: "info",
          code: "Bath placement",
          msg: "Avoid direct splash zones, shower spray and heavy steam. Use suitable framing and ventilation for bathroom placement."
        };
      case "KITCHEN_PLACEMENT_CHECK":
        return {
          type: "info",
          code: "Kitchen check",
          msg: "Before hanging, avoid hobs, sinks, steam, grease, wall cupboards and sockets, and check the wall before drilling."
        };
      case "TALL_FURNITURE_VIEWING_CHECK":
        return {
          type: "info",
          code: "Tall furniture",
          msg: "Because this furniture is tall, check the artwork still feels comfortable at normal viewing height before fixing."
        };
      default:
        return {
          type: "info",
          code: warning.code.replace(/_/g, " "),
          msg: "A calculator warning was returned."
        };
    }
  }

  function errorToAlert(error) {
    const fields = (error.meta && error.meta.fields) || [];

    if (error.code === "INVALID_INPUTS") {
        return {
          type: "error",
          code: "Check inputs",
          msg: fields.length
            ? "Some measurements are missing or invalid: " + fields.join(", ") + "."
            : "Some measurements are missing or invalid."
      };
    }

    if (error.code === "INPUT_VALIDATION") {
      return {
        type: "error",
        code: "Check inputs",
        msg: error.meta && error.meta.message
          ? error.meta.message
          : "Please check the measurements before calculating."
      };
    }

    if (error.code === "NO_VIABLE_LAYOUT") {
      return {
        type: "error",
        code: "No fit found",
        msg: "No valid artwork layout fits these measurements under the current rules."
      };
    }

    return {
      type: "error",
      code: error.code.replace(/_/g, " "),
      msg: "The calculator could not build a recommendation."
    };
  }

  function assumptionToAlert(assumption) {
    switch (assumption.code) {
      case "ROOM_PRESET_WALL_WIDTH":
        return null;
      case "ROOM_PRESET_CEILING_HEIGHT":
        return null;
      case "FURNITURE_DEFAULT_WIDTH":
        return null;
      case "FURNITURE_DEFAULT_TOP_HEIGHT":
        return null;
      case "DEFAULT_ANCHOR_CENTRE_X":
        return null;
      case "NO_HEADBOARD_MATTRESS_TOP_FALLBACK":
        return {
          type: "info",
          code: "Assumption",
          msg: "Used mattress top height because no headboard height was supplied."
        };
      default:
        return {
          type: "info",
          code: "ASSUMPTION",
          msg: assumption.code.replace(/_/g, " ").toLowerCase() + "."
        };
    }
  }

  function buildAlerts(engineResult) {
    const alerts = [];
    const selectedSizeOption = engineResult.selectedSizeOption || "recommended";
    const optionSpecificWarnings = [
      "UNDERSIZED",
      "ABOVE_WIDTH_BAND",
      "CLEARANCE_TOO_HIGH",
      "ORIENTATION_CHANGED_FOR_FIT",
      "LAYOUT_CHANGED_FOR_FIT",
      "LOW_CEILING_LIMITED_SIZE_OPTIONS"
    ];
    const warnings = (engineResult.warnings || []).filter(function (warning) {
      return selectedSizeOption === "recommended" ||
        optionSpecificWarnings.indexOf(warning.code) === -1;
    });

    alerts.push.apply(alerts, (engineResult.errors || []).map(errorToAlert));
    alerts.push.apply(alerts, warnings.map(warningToAlert).filter(Boolean));
    alerts.push.apply(alerts, (engineResult.assumptionsUsed || []).map(assumptionToAlert).filter(Boolean));
    return alerts;
  }

  function isValidationOnlyErrorResult(engineResult) {
    const errors = engineResult.errors || [];

    return engineResult.status === "error" &&
      errors.length > 0 &&
      errors.every(function (error) {
        return error.code === "INPUT_VALIDATION";
      }) &&
      !(engineResult.warnings || []).length &&
      !(engineResult.assumptionsUsed || []).length;
  }

  function renderAlerts(engineResult) {
    if (isValidationOnlyErrorResult(engineResult)) {
      App.dom.alertsZone.innerHTML = "";
      return;
    }

    const alerts = buildAlerts(engineResult);

    App.dom.alertsZone.innerHTML = alerts.length
      ? "<h2 class=\"notes-heading\">Useful notes</h2>" + alerts.map(function (alert) {
        return "<div class=\"alert " + alertClass(alert.type) + "\">" + escapeHtml(alert.msg) + "</div>";
      }).join("")
      : "";
  }

  function renderErrorState(engineResult, shouldScrollToResults) {
    const validationError = (engineResult.errors || []).find(function (error) {
      return error.code === "INPUT_VALIDATION" && error.meta && error.meta.message;
    });
    App.dom.recBlock.classList.add("recommendation-error");
    App.dom.metricsGrid.innerHTML = "";
    App.dom.rangeMin.textContent = "—";
    App.dom.rangeMax.textContent = "—";
    App.dom.rangeBarFill.style.width = "0%";
    App.dom.rangeBarIdeal.style.left = "0%";
    App.dom.recBlock.innerHTML =
      "<h2 class=\"rec-header\">Recommendation unavailable</h2>" +
      "<div class=\"rec-title\">Check your measurements</div>" +
      "<div class=\"rec-subtitle\"" + (validationError ? " role=\"alert\"" : "") + ">" + (validationError
        ? escapeHtml(validationError.meta.message)
        : "The calculator could not produce a valid result from the current inputs.") + "</div>";
    App.dom.hangingInstructions.innerHTML = "";
    renderShopCtaBlock(null);
    App.dom.previewNote.textContent = "—";
    App.dom.svgPreviewStage.innerHTML = "";
    App.dom.previewControls.style.display = "none";
    App.dom.roomPreview.style.display = "none";
    App.dom.keyMeasurementsLabel.style.display = "none";
    App.dom.metricsGrid.style.display = "none";
    App.dom.rangeBarWrap.style.display = "none";
    App.dom.installationSection.style.display = "none";

    if (engineResult.status === "error" && shouldScrollToResults) {
      setTimeout(function () {
        App.dom.resultsPanel.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }

  function renderMetrics(engineResult) {
    const recommendation = engineResult.recommendation;
    const artwork = engineResult.previewGeometry.artwork;
    const idealPercent = Math.round(percentOfAnchor(recommendation.idealWidthCm, engineResult) * 100);
    const sizeText = recommendation.isoSize + " " + orientationLabel(recommendation.orientation);
    const fourthCard = isDiningTableDisplay(engineResult)
      ? "<div class=\"metric-card\"><div class=\"metric-label\">Width reference</div><div class=\"metric-value metric-value-text\">Dining table</div><div class=\"metric-unit\">artwork centred to table</div></div>"
      : isCentreHeightFurnitureDisplay(engineResult)
      ? "<div class=\"metric-card\"><div class=\"metric-label\">Placement basis</div><div class=\"metric-value metric-value-text\">Centre height</div><div class=\"metric-unit\">no fixed furniture gap</div></div>"
      : hasFurniture(engineResult)
      ? "<div class=\"metric-card\"><div class=\"metric-label\">Gap above furniture</div><div class=\"metric-value\">" + escapeHtml(App.formatValue(artwork.clearanceAboveFurnitureCm)) + "</div><div class=\"metric-unit\">" + escapeHtml(App.unitLabel()) + " clear space</div></div>"
      : "<div class=\"metric-card\"><div class=\"metric-label\">Ideal width</div><div class=\"metric-value\">" + escapeHtml(App.formatValue(recommendation.idealWidthCm)) + "</div><div class=\"metric-unit\">" + escapeHtml(App.unitLabel()) + " · " + escapeHtml(idealPercent) + "% of " + escapeHtml(anchorLabel(engineResult)) + "</div></div>";

    App.dom.metricsGrid.innerHTML =
      "<div class=\"metric-card metric-card-primary\"><div class=\"metric-label\">Frame size</div><div class=\"metric-value metric-value-text\">" + escapeHtml(sizeText) + "</div><div class=\"metric-unit\">" + escapeHtml(artwork.pieceCount) + " piece" + (artwork.pieceCount > 1 ? "s" : "") + " · " + escapeHtml(App.formatValue(artwork.pieceOuterWidthCm)) + " × " + escapeHtml(App.formatValue(artwork.pieceOuterHeightCm)) + " " + escapeHtml(App.unitLabel()) + "</div></div>" +
      "<div class=\"metric-card\"><div class=\"metric-label\">Total width</div><div class=\"metric-value\">" + escapeHtml(App.formatValue(artwork.totalArtworkWidthCm)) + "</div><div class=\"metric-unit\">" + escapeHtml(App.unitLabel()) + " across full layout</div></div>" +
      "<div class=\"metric-card\"><div class=\"metric-label\">Centre height</div><div class=\"metric-value\">" + escapeHtml(App.formatValue(artwork.centreFromFloorCm)) + "</div><div class=\"metric-unit\">" + escapeHtml(App.unitLabel()) + " from floor</div></div>" +
      fourthCard;
  }

  function renderRange(engineResult) {
    const recommendation = engineResult.recommendation;
    const artwork = engineResult.previewGeometry.artwork;
    const rangeWidth = recommendation.maximumWidthCm - recommendation.minimumWidthCm;

    function rangePercent(value) {
      if (rangeWidth <= 0) return 0;
      return clamp(((value - recommendation.minimumWidthCm) / rangeWidth) * 100, 0, 100);
    }

    App.dom.rangeMin.textContent = formatMeasurement(recommendation.minimumWidthCm) + " minimum";
    App.dom.rangeMax.textContent = formatMeasurement(recommendation.maximumWidthCm) + " maximum";
    App.dom.rangeBarFill.style.width = rangePercent(artwork.totalArtworkWidthCm) + "%";
    App.dom.rangeBarIdeal.style.left = rangePercent(recommendation.idealWidthCm) + "%";
  }

  function sizeOptionDescription(key) {
    if (key === "safe") return "A more subtle fit";
    if (key === "statement") return "A bolder fit";
    return "Best balanced fit";
  }

  function selectedOptionLabel(key) {
    if (key === "safe") return "Safe";
    if (key === "statement") return "Statement";
    return "Recommended";
  }

  function artworkAccessibleSummary(recommendation, artwork) {
    const pieceLabel = artwork.pieceCount === 1 ? "print" : "prints";
    return artwork.pieceCount + " " + recommendation.isoSize + " " + recommendation.orientation + " " + pieceLabel +
      ", " + accessibleLayoutLabel(recommendation.layout) + " layout, " +
      formatAccessibleMeasurement(artwork.totalArtworkWidthCm) + " total width";
  }

  function updateResultsStatus(message) {
    if (!App.dom.resultsStatus || App.dom.resultsStatus.textContent === message) {
      return;
    }

    App.dom.resultsStatus.textContent = message;
  }

  function resultStatusMessage(engineResult) {
    if (!engineResult || engineResult.status !== "ok") {
      return "Recommendation unavailable. Check the highlighted field.";
    }

    const selectedKey = engineResult.selectedSizeOption || "recommended";
    const action = selectedKey === "recommended" ? "updated" : "selected";

    return selectedOptionLabel(selectedKey) + " size " + action + ": " +
      artworkAccessibleSummary(engineResult.recommendation, engineResult.previewGeometry.artwork) + ".";
  }

  function roomCollectionLink(engineResult) {
    const roomType = engineResult && engineResult.resolvedInputs && engineResult.resolvedInputs.roomType;
    return ROOM_COLLECTION_LINKS[roomType] || FALLBACK_COLLECTION_LINK;
  }

  function buildShopCta(engineResult) {
    const recommendation = engineResult && engineResult.recommendation;

    if (!recommendation || !recommendation.isoSize || !recommendation.orientation) {
      return null;
    }

    const sizeParam = encodeURIComponent(recommendation.isoSize);
    const orientationParam = encodeURIComponent(recommendation.orientation);
    const layoutParam = recommendation.layout === "pair" || recommendation.layout === "triptych"
      ? "&layout=" + encodeURIComponent(recommendation.layout)
      : "";
    const secondaryLink = roomCollectionLink(engineResult);

    return {
      primaryText: "Shop " + recommendation.isoSize + " " + orientationLabel(recommendation.orientation) + " Prints",
      primaryUrl: SHOP_CTA_BASE_COLLECTION + "?size=" + sizeParam + "&orientation=" + orientationParam + layoutParam,
      secondaryText: secondaryLink.text,
      secondaryUrl: secondaryLink.url
    };
  }

  function renderShopCta(engineResult) {
    const cta = buildShopCta(engineResult);

    if (!cta) {
      return "";
    }

    return "<div class=\"shop-cta\" aria-label=\"Shop this recommendation\">" +
      "<div class=\"shop-cta-title\">Ready to choose artwork?</div>" +
      "<div class=\"shop-cta-copy\">Start with prints in your recommended size, or browse by room.</div>" +
      "<div class=\"shop-cta-actions\">" +
      "<a class=\"shop-cta-primary\" href=\"" + escapeAttribute(cta.primaryUrl) + "\">" + escapeHtml(cta.primaryText) + "</a>" +
      "<a class=\"shop-cta-secondary\" href=\"" + escapeAttribute(cta.secondaryUrl) + "\">" + escapeHtml(cta.secondaryText) + "</a>" +
      "</div>" +
      "</div>";
  }

  function renderShopCtaBlock(engineResult) {
    const wrap = document.getElementById("shopCtaWrap");
    const block = document.getElementById("shopCtaBlock");
    const html = engineResult && engineResult.status === "ok"
      ? renderShopCta(engineResult)
      : "";

    if (!wrap || !block) {
      return;
    }

    block.innerHTML = html;
    wrap.style.display = html ? "" : "none";
  }

  function renderSizeOption(option, selectedKey) {
    const recommendation = option.recommendation;
    const artwork = option.artwork;
    const isSelected = option.key === selectedKey;
    const frameText = artwork.pieceCount + " × " + recommendation.isoSize + " " + orientationLabel(recommendation.orientation);
    const selectedClass = isSelected ? " is-selected" : "";
    const statusText = isSelected ? "<span class=\"size-option-status\">Shown in preview</span>" : "";
    const ariaLabel = selectedOptionLabel(option.key) + " option" + (isSelected ? ", selected" : "") + ". " +
      artworkAccessibleSummary(recommendation, artwork) + ".";

    return "<button type=\"button\" class=\"size-option-card" + selectedClass + "\" data-size-option=\"" + escapeAttribute(option.key) + "\" aria-pressed=\"" + isSelected + "\" aria-label=\"" + escapeAttribute(ariaLabel) + "\">" +
      "<span class=\"size-option-kicker\">" + escapeHtml(sizeOptionDescription(option.key)) + "</span>" +
      "<span class=\"size-option-title\">" + escapeHtml(option.label) + "</span>" +
      "<span class=\"size-option-detail\">" + escapeHtml(frameText) + " · " + escapeHtml(layoutLabel(recommendation.layout)) + " · " + escapeHtml(formatMeasurement(artwork.totalArtworkWidthCm)) + " total width</span>" +
      statusText +
      "</button>";
  }

  function renderSizeOptionsComparison(engineResult) {
    const sizeOptions = engineResult.sizeOptions;

    if (!sizeOptions || !sizeOptions.recommended) {
      return "";
    }

    const selectedKey = engineResult.selectedSizeOption || "recommended";
    const options = [];
    if (sizeOptions.safe) options.push(renderSizeOption(sizeOptions.safe, selectedKey));
    options.push(renderSizeOption(sizeOptions.recommended, selectedKey));
    if (sizeOptions.statement) options.push(renderSizeOption(sizeOptions.statement, selectedKey));

    return "<h3 class=\"rec-proof-label\">Size choices</h3>" +
      "<div class=\"size-options-list\">" +
      options.join("") +
      "</div>" +
      "<div class=\"rec-confidence\">Preview shows " + escapeHtml(selectedOptionLabel(selectedKey)) + ".</div>";
  }

  function renderRecommendation(engineResult) {
    const recommendation = engineResult.recommendation;
    const artwork = engineResult.previewGeometry.artwork;
    const selectedKey = engineResult.selectedSizeOption || "recommended";
    const minPercent = Math.round(percentOfAnchor(recommendation.minimumWidthCm, engineResult) * 100);
    const maxPercent = Math.round(percentOfAnchor(recommendation.maximumWidthCm, engineResult) * 100);
    const footprintPercent = Math.round((artwork.totalArtworkWidthCm / anchorWidth(engineResult)) * 100);
    const subtitleParts = [
      layoutLabel(recommendation.layout) + " layout",
      formatMeasurement(artwork.totalArtworkWidthCm) + " total width"
    ];
    const confidenceLine = hasFurniture(engineResult)
      ? "Balanced for your " + furnitureLabel(engineResult) + " and wall width."
      : "Balanced for your usable wall width.";

    if (isDiningTableDisplay(engineResult)) {
      subtitleParts.push("centred at " + formatMeasurement(artwork.centreFromFloorCm) + ", aligned with your dining table");
    } else if (isCentreHeightFurnitureDisplay(engineResult)) {
      subtitleParts.push(centreHeightPlacementText(engineResult));
    } else if (hasFurniture(engineResult)) {
      subtitleParts.push(formatMeasurement(artwork.clearanceAboveFurnitureCm) + " above " + furnitureLabel(engineResult));
    }

    const proofSentence = artwork.pieceCount > 1
      ? "With " + escapeHtml(artwork.pieceCount) + " frames and a " + escapeHtml(formatMeasurement(artwork.spacingCm)) + " gap, the total display footprint reaches " + escapeHtml(formatMeasurement(artwork.totalArtworkWidthCm)) + ", occupying " + escapeHtml(footprintPercent) + "% of the " + escapeHtml(anchorLabel(engineResult)) + " width."
      : "This single " + escapeHtml(recommendation.isoSize) + " " + escapeHtml(orientationLabel(recommendation.orientation)) + " frame creates a " + escapeHtml(formatMeasurement(artwork.totalArtworkWidthCm)) + " wide display and occupies " + escapeHtml(footprintPercent) + "% of your " + escapeHtml(anchorLabel(engineResult)) + " width.";

    App.dom.recBlock.classList.remove("recommendation-error");
    App.dom.recBlock.innerHTML =
      "<h2 class=\"rec-header\">" + escapeHtml(selectedOptionLabel(selectedKey)) + " size</h2>" +
      "<div class=\"rec-title\">" + escapeHtml(artwork.pieceCount) + " × " + escapeHtml(recommendation.isoSize) + " " + escapeHtml(orientationLabel(recommendation.orientation)) + "</div>" +
      "<div class=\"rec-subtitle\">" + escapeHtml(subtitleParts.join(" · ")) + "</div>" +
      "<div class=\"rec-confidence\">" + escapeHtml(confidenceLine) + "</div>" +
      renderSizeOptionsComparison(engineResult) +
      "<h3 class=\"rec-proof-label\">Why this works</h3>" +
      "<div class=\"rec-body\">" +
      "Based on your " + escapeHtml(anchorLabel(engineResult)) + " width of " + escapeHtml(formatMeasurement(anchorWidth(engineResult))) + ", the target artwork band is <strong style=\"font-weight:400\">" + escapeHtml(minPercent) + "%–" + escapeHtml(maxPercent) + "%</strong>, or <strong style=\"font-weight:400\">" + escapeHtml(formatMeasurement(recommendation.minimumWidthCm)) + "–" + escapeHtml(formatMeasurement(recommendation.maximumWidthCm)) + "</strong>. " +
      proofSentence +
      "</div>";
    renderShopCtaBlock(engineResult);
  }

  function bindSizeOptionControls() {
    if (!App.dom.recBlock || typeof App.selectSizeOption !== "function") {
      return;
    }

    App.dom.recBlock.querySelectorAll("[data-size-option]").forEach(function (control) {
      control.addEventListener("click", function () {
        App.selectSizeOption(control.getAttribute("data-size-option"));
      });
    });
  }

  function renderHangingInstructions(engineResult) {
    const artwork = engineResult.previewGeometry.artwork;
    const steps = [
      "Mark a light horizontal centre line at " + formatMeasurement(artwork.centreFromFloorCm) + " from the floor."
    ];

    if (isDiningTableDisplay(engineResult)) {
      steps.push("Align the artwork with the centre of your dining table; the table is used for width and centring, not as a gap marker.");
      steps.push("Preview shows the furniture centred on the wall. In your room, hang the artwork centred over your furniture.");
    } else if (isBathDisplay(engineResult)) {
      steps.push("Use the centre-height line first, then check the clear space above the bath.");
      steps.push("Preview shows the furniture centred on the wall. In your room, hang the artwork centred over your furniture.");
    } else if (isCentreHeightFurnitureDisplay(engineResult)) {
      steps.push("Use this centre line as the placement guide; the clear space above the " + furnitureLabel(engineResult) + " is derived from that centre-height placement.");
      steps.push("Preview shows the furniture centred on the wall. In your room, hang the artwork centred over your furniture.");
    } else if (hasFurniture(engineResult)) {
      steps.push("Position the layout so the bottom edge sits " + formatMeasurement(artwork.clearanceAboveFurnitureCm) + " above the " + furnitureLabel(engineResult) + ".");
      steps.push("Preview shows the furniture centred on the wall. In your room, hang the artwork centred over your furniture.");
    } else {
      steps.push("Centre the artwork on the usable wall area, leaving balanced space on both sides.");
    }

    steps.push(
      artwork.pieceCount > 1
        ? "Keep each frame spaced " + formatMeasurement(artwork.spacingCm) + " apart so the layout reads as one piece."
        : "Keep the single frame level and centred on your marked point."
    );

    steps.push(
      "Tape paper templates first, then step back and check the balance from normal viewing distance.",
      "Only mark fixing points once the placement feels calm, level, and balanced."
    );

    App.dom.hangingInstructions.innerHTML =
      "<ol class=\"guide-list\">" +
      steps.map(function (step) {
        return "<li>" + escapeHtml(step) + "</li>";
      }).join("") +
      "</ol>";
  }

  App.renderResults = function (engineResult, options) {
    const panel = App.dom.resultsPanel;
    const shouldScrollToResults = !!(options && options.scrollToResults);

    panel.style.display = "block";
    panel.classList.add("visible");

    renderAlerts(engineResult);

    if (engineResult.status !== "ok") {
      renderErrorState(engineResult, shouldScrollToResults);
      updateResultsStatus(resultStatusMessage(engineResult));
      return;
    }

    App.dom.previewControls.style.display = "";
    App.dom.roomPreview.style.display = "";
    App.dom.keyMeasurementsLabel.style.display = "";
    App.dom.metricsGrid.style.display = "";
    App.dom.rangeBarWrap.style.display = "";
    App.dom.installationSection.style.display = "";

    setTimeout(function () {
      if (engineResult.status === "ok") {
        App.renderRoomPreview(engineResult);
      }
    }, 50);

    renderMetrics(engineResult);
    renderRange(engineResult);
    renderRecommendation(engineResult);
    bindSizeOptionControls();
    renderHangingInstructions(engineResult);
    updateResultsStatus(resultStatusMessage(engineResult));

    if (shouldScrollToResults) {
      setTimeout(function () {
        panel.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  };
}());
